import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getNavigation, getAppToken, recordPageView } from '../api/client.js';
import { useSession } from '../context/SessionContext.jsx';
import {
  framedUrl, replyToFrameMessage, themeMessage
} from '../lib/embedTheme.js';

/**
 * Hosts an embedded third-party site.
 *
 * Three changes from the old implementation:
 *
 * 1. The URL is not taken from the DOM. The old dashClient read a data-id
 *    attribute off the clicked <a> and dropped it straight into iframe src,
 *    so anything that could write to the DOM could frame anything. Here the
 *    route carries a profile item id and the URL comes from the API, which
 *    has already checked the item belongs to the user's profile.
 *
 * 2. The iframe is sandboxed. Embedded partner apps get scripts, forms and
 *    same-origin, but not top-navigation, popups or downloads.
 *
 * 3. The theme is handed across the boundary — see lib/embedTheme.js. Nothing
 *    cascades into an iframe, so before this an embedded app had no way to
 *    know the portal was in dark mode.
 */
export default function WebsiteView() {
  const { itemId } = useParams();
  const { theme } = useSession();
  const [frame, setFrame] = useState(null);      // { src, origin }
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  /* The current theme, in a ref.
     Assigned during render, so any effect reading it after that render sees
     the live value — but reading a ref does not subscribe, which is the whole
     point: see the dependency array below. */
  const themeRef = useRef(theme);
  themeRef.current = theme;

  const { data: nav } = useQuery({
    queryKey: ['navigation', 'Desktop'],
    queryFn: () => getNavigation('Desktop')
  });

  const item = nav?.groups
    .flatMap(g => g.items)
    .find(i => String(i.id) === String(itemId));

  /* Building the frame.
   *
   * The dependency array is `[item]` and must stay that way. Reading `theme`
   * here and adding it to the deps looks like the obvious way to keep the URL
   * current, and it is a trap: every toggle would re-run this, change `src`,
   * RELOAD the frame — discarding whatever state the app held — and call
   * getAppToken again, burning a five-minute credential per click of the
   * theme button.
   *
   * So the query parameter is set once, from the theme at the moment the frame
   * is built, and every subsequent change travels by postMessage in the effect
   * below. Reading themeRef rather than `theme` is what keeps it out of the
   * deps — and reading it here rather than at first render also means a theme
   * that arrives late from /api/me still reaches the URL.
   */
  useEffect(() => {
    let cancelled = false;
    setFrame(null);
    setError(null);
    if (!item) return;

    (async () => {
      try {
        if (item.requiresAppToken) {
          // Short-lived, audience-bound token minted server side.
          const { url, token } = await getAppToken(item.id);
          if (cancelled) return;
          setFrame(framedUrl({ targetUrl: url, token, theme: themeRef.current }));
        } else {
          /* The no-token branch needs the theme too. It used to set src
             straight from item.targetUrl with no URL construction at all,
             which is exactly how a change like this gets made in one branch
             and forgotten in the other. */
          setFrame(framedUrl({ targetUrl: item.targetUrl, theme: themeRef.current }));
          // Fire and forget; telemetry must not block the view.
          recordPageView(item.id).catch(() => {});
        }
      } catch (err) {
        if (!cancelled) setError(err);
      }
    })();

    return () => { cancelled = true; };
  }, [item]);

  /* Telling the app about a change.
   *
   * Skipped on the first run: the URL already carries that value, and posting
   * it again before the app has loaded its listener achieves nothing. After
   * that, every toggle posts — at the frame's own origin, never '*', because
   * this message says something about the user's session and only the app we
   * framed has any business reading it.
   */
  /* Has the frame signalled anything at all?
     -----------------------------------------------------------------------
     The portal cannot see inside a cross-origin iframe, so it cannot tell a
     working page from the target's own 404 - a visitor to a dead link gets
     that site's raw JSON with no idea what they are looking at or who to tell.

     What it CAN tell is that the frame never loaded: onLoad does not fire for a
     CSP block, a DNS failure or a refused connection. That is the case worth
     catching, because it is the one with no visible explanation whatsoever -
     an empty white panel. */
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    setFrameLoaded(false);
    setSlow(false);
    if (!frame) return;
    const t = setTimeout(() => setSlow(true), 12_000);
    return () => clearTimeout(t);
  }, [frame]);

  const posted = useRef(false);
  useEffect(() => {
    if (!frame) { posted.current = false; return; }
    if (!posted.current) { posted.current = true; return; }

    iframeRef.current?.contentWindow?.postMessage(themeMessage(theme), frame.origin);
  }, [theme, frame]);

  /* Answering an app that announces itself.
   *
   * A module script is deferred by specification, so a partner app's listener
   * may not exist until after we minted its URL — and the theme may have
   * changed in between. `vizora:ready` closes that gap, and is also how an app
   * framed by an older portal build still ends up themed.
   *
   * Keyed on the origin alone, not on the theme: re-subscribing on every
   * toggle would drop a message arriving mid-swap. The current value is read
   * from themeRef at the moment we answer.
   */
  useEffect(() => {
    if (!frame) return;

    function onMessage(event) {
      const reply = replyToFrameMessage(event, {
        frameOrigin: frame.origin,
        frameWindow: iframeRef.current?.contentWindow,
        theme: themeRef.current
      });
      if (reply) event.source?.postMessage(themeMessage(reply), frame.origin);
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frame]);

  if (!nav) return <div className="embed-overlay"><div className="spinner-border text-primary" /></div>;

  if (!item) {
    return <div className="admin-page">
      <div className="alert alert-warning">That item is no longer available on your profile.</div>
    </div>;
  }

  if (error) {
    return <div className="admin-page">
      <div className="alert alert-danger">Could not open {item.label}: {error.message}</div>
    </div>;
  }

  return (
    <div className="embed-wrapper">
      {/* No toolbar. An embedded site is the whole reason the page exists, and a
          bar naming it repeated what the menu entry the user just clicked
          already said - at the cost of ~46px of a frame that is often a dense
          application in its own right.

          What that bar also carried was the origin and an "open in a new tab"
          link, which mattered for the case where the target renders its own
          error page and the portal cannot tell. That has moved into the failure
          state below, which is where it was actually needed: when the frame is
          working, nobody wants either. */}
      {!frame && <div className="embed-overlay"><div className="spinner-border text-primary" /></div>}

      {/* Only shown when the frame has genuinely not loaded. An application that
          loaded and then showed its own error is not something we can detect,
          and claiming otherwise would be worse than saying nothing. */}
      {frame && slow && !frameLoaded && (
        <div className="embed-overlay">
          <div className="embed-stalled">
            <i className="bi bi-exclamation-triangle" aria-hidden="true" />
            <p className="fw-semibold mb-1">{item.label} is not responding</p>
            <p className="mb-2">
              The portal asked <code>{frame.origin}</code> for this page and it has not
              loaded. That usually means the application is down, the address has
              changed, or it is not permitted to be embedded.
            </p>
            <a className="btn btn-sm btn-primary" href={frame.src}
               target="_blank" rel="noopener noreferrer">Try it in a new tab</a>
            <p className="embed-stalled-note mb-0">
              If it works in a new tab but not here, tell Vizora — the address
              needs adding to the portal&rsquo;s embedding rules.
            </p>
          </div>
        </div>
      )}

      {frame && (
        <iframe
          ref={iframeRef}
          title={item.label}
          src={frame.src}
          className="embed-container"
          sandbox="allow-scripts allow-forms allow-same-origin allow-downloads"
          referrerPolicy="strict-origin"
          allowFullScreen
          onLoad={() => setFrameLoaded(true)}
        />
      )}
    </div>
  );
}
